import { useState } from 'react';
import { Button } from '@clay-run/glaze/button';
import { InputField } from '@clay-run/glaze/input-field';
import { SegmentedControl, SegmentedControlOption } from '@clay-run/glaze/segmented-control';
import { Text } from '@clay-run/glaze/text';
import { Copy } from '@phosphor-icons/react/dist/icons/Copy';
import { Plus } from '@phosphor-icons/react/dist/icons/Plus';
import { AUTOFILL_IGNORE_PROPS } from '../../_shared/autofill';
import { useDuplicateLeadForm, useMetaLeadForms } from '../api-client';
import { withLeadForm } from '../draft';
import type { MetaDraft } from '../draft-fixtures';
import { growthFormDefaults, questionLabel } from '../lead-forms';
import { LeadFormEditor } from './LeadFormEditor';

/**
 * The ad's Instant Form, like the block on Meta's ad screen: create Growth's standard form, or
 * duplicate one of the Page's forms, then edit every field. Meta creates it when the request is published.
 */
export function LeadFormCard({ draft, onChange, enabled }: { draft: MetaDraft; onChange: (d: MetaDraft) => void; enabled: boolean }) {
  const forms = useMetaLeadForms(enabled);
  const duplicate = useDuplicateLeadForm();
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [editing, setEditing] = useState(0);
  const form = draft.leadForm;
  const q = query.trim().toLowerCase();
  const listed = (forms.data ?? [])
    .filter(f => (tab === 'archived' ? f.status === 'ARCHIVED' : f.status !== 'ARCHIVED'))
    .filter(f => !q || f.name.toLowerCase().includes(q));
  const sourceName = form?.sourceId ? forms.data?.find(f => f.id === form.sourceId)?.name : null;
  const usedAsIs = !form && draft.leadFormId ? (draft.leadFormName ?? forms.data?.find(f => f.id === draft.leadFormId)?.name ?? `Form ${draft.leadFormId}`) : null;

  const startEditing = (next: MetaDraft) => {
    onChange(next);
    setEditing(n => n + 1);
  };
  const duplicateForm = (formId: string) =>
    duplicate.mutate(formId, { onSuccess: spec => startEditing(withLeadForm(draft, spec, formId)) });

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border-primary p-3.5">
      <div className="flex flex-col gap-0.5">
        <Text size="sm" weight="medium">
          Instant form
        </Text>
        <Text size="xs" variant="tertiary">
          People send their contact information in a form inside Facebook or Instagram. Meta creates the form when the request is
          published, since forms can't be changed afterwards.
        </Text>
      </div>

      {form && (
        <div className="flex items-start gap-3 rounded-lg bg-bg-secondary p-3">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-sm font-medium">{form.spec.name}</span>
            <span className="truncate text-xs text-content-tertiary">
              {form.spec.questions.map(questionLabel).join(', ')} · ends with “{form.spec.thankYou.title}”
            </span>
            <span className="text-xs text-content-tertiary">
              {form.sourceId ? `Duplicated from ${sourceName ?? `form ${form.sourceId}`}` : "Growth's standard form"}
              {form.spec.questionHeadline ? '' : ' · no description yet'}
            </span>
          </div>
          <Button size="sm" variant="secondary" onClick={() => setEditing(n => n + 1)}>
            Edit
          </Button>
        </div>
      )}
      {usedAsIs && draft.leadFormId && (
        <div className="flex items-start gap-3 rounded-lg bg-bg-secondary p-3">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-sm font-medium">{usedAsIs}</span>
            <span className="text-xs text-content-tertiary">Used as it is. Duplicate it to change anything.</span>
          </div>
          <Button size="sm" variant="secondary" loading={duplicate.isPending} onClick={() => draft.leadFormId && duplicateForm(draft.leadFormId)}>
            Duplicate to edit
          </Button>
        </div>
      )}

      <div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => startEditing(withLeadForm(draft, growthFormDefaults(draft.program), null))}
        >
          <Plus size={14} /> Create instant form
        </Button>
      </div>

      <InputField {...AUTOFILL_IGNORE_PROPS} label="Search your forms" placeholder="Search" value={query} onValueChange={setQuery} />
      <SegmentedControl value={tab} onValueChange={v => setTab(v === 'archived' ? 'archived' : 'active')}>
        <SegmentedControlOption value="active">Active</SegmentedControlOption>
        <SegmentedControlOption value="archived">Archived</SegmentedControlOption>
      </SegmentedControl>
      <div className="flex max-h-64 flex-col overflow-y-auto">
        {!enabled && (
          <Text size="xs" variant="tertiary">
            Connect Meta to see the Page's forms.
          </Text>
        )}
        {forms.isLoading && (
          <Text size="xs" variant="tertiary">
            Loading forms…
          </Text>
        )}
        {forms.isError && (
          <Text size="xs" className="text-text-red">
            Clay's Page forms couldn't be loaded: {forms.error.message}
          </Text>
        )}
        {forms.data && listed.length === 0 && (
          <Text size="xs" variant="tertiary">
            No {tab} forms{q ? ` matching “${query.trim()}”` : ''}.
          </Text>
        )}
        {listed.map(f => (
          <div key={f.id} className="flex items-center gap-2 border-b border-border-primary py-2 last:border-b-0">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm">{f.name}</span>
              {f.createdTime && <span className="text-xs text-content-tertiary">Created on {f.createdTime.slice(0, 10)}</span>}
            </div>
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Duplicate ${f.name}`}
              loading={duplicate.isPending && duplicate.variables === f.id}
              onClick={() => duplicateForm(f.id)}
            >
              <Copy size={14} /> Duplicate
            </Button>
          </div>
        ))}
      </div>
      {duplicate.error && (
        <Text size="xs" className="text-text-red">
          That form couldn't be read: {duplicate.error.message}
        </Text>
      )}

      {editing > 0 && form && (
        <LeadFormEditor
          key={editing}
          initial={form.spec}
          onClose={() => setEditing(0)}
          onSave={spec => {
            onChange({ ...draft, leadForm: { ...form, spec, nameEdited: form.nameEdited || spec.name !== form.spec.name } });
            setEditing(0);
          }}
        />
      )}
    </div>
  );
}
